"use client";

import { cn } from "@/lib/utils";

export type CapacityColor = "emerald" | "blue" | "amber" | "rose" | "purple";
export type CapacitySize = "xs" | "sm" | "md";

export interface CapacityBarProps {
  /** Progress value (0-100) */
  value: number;
  /** Optional label text */
  label?: string;
  /** Whether to show the percentage value */
  showPercentage?: boolean;
  /** Size variant */
  size?: CapacitySize;
  /** Color of the filled bar */
  color?: CapacityColor;
  /** Show on dark background (glass style) */
  glass?: boolean;
  /** Additional CSS classes */
  className?: string;
}

const colorClasses: Record<CapacityColor, string> = {
  emerald: "bg-emerald-400",
  blue: "bg-blue-400",
  amber: "bg-amber-400",
  rose: "bg-rose-400",
  purple: "bg-purple-400",
};

const sizeClasses: Record<CapacitySize, { bar: string; width: string }> = {
  xs: { bar: "h-1", width: "w-12" },
  sm: { bar: "h-2", width: "w-16" },
  md: { bar: "h-3", width: "w-24" },
};

/**
 * CapacityBar - Progress/capacity visualization
 *
 * Use this component to show fill level, capacity, or progress.
 * Supports both regular and glass (for dark backgrounds) variants.
 *
 * @example
 * // Regular usage
 * <CapacityBar value={75} label="Capacity" showPercentage />
 *
 * // Glass style on image/gradient background
 * <CapacityBar value={50} size="sm" glass />
 */
export function CapacityBar({
  value,
  label,
  showPercentage = false,
  size = "sm",
  color = "emerald",
  glass = false,
  className,
}: CapacityBarProps) {
  // Clamp value between 0 and 100
  const clampedValue = Math.min(100, Math.max(0, value));

  const sizeConfig = sizeClasses[size];

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {label && (
        <span
          className={cn(
            "text-xs",
            glass ? "text-gray-300" : "text-muted-foreground"
          )}
        >
          {label}
        </span>
      )}

      {/* Track */}
      <div
        className={cn(
          "rounded-full overflow-hidden",
          sizeConfig.width,
          sizeConfig.bar,
          glass ? "bg-white/30" : "bg-muted"
        )}
      >
        {/* Fill */}
        <div
          className={cn(
            "h-full rounded-full transition-all duration-500",
            colorClasses[color]
          )}
          style={{ width: `${clampedValue}%` }}
        />
      </div>

      {showPercentage && (
        <span
          className={cn(
            "text-xs font-semibold",
            glass ? "text-white" : "text-foreground"
          )}
        >
          {Math.round(clampedValue)}%
        </span>
      )}
    </div>
  );
}

export default CapacityBar;
